import { useContext, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Menu as MenuIcon } from '@mui/icons-material';
import { isUndefined } from 'lodash';
import { useTranslation } from 'react-i18next';
import { datadogLogs } from '@datadog/browser-logs';
import { AppBreadcrumbs } from './appBreadcrumbs';
import OrganizationLogo from './organizationLogo';
import { Box } from '../../1-primative';
import { AppBar, IconButton, Toolbar } from '../../2-component';
import EntityMenu from '../../3-pattern/entityMenu/entityMenu';
import { Notifications } from '../notifications/notifications';
import { usePermissions, UserContext } from '../../../providers/userContextProvider';
import { useThemeTokens } from '../../../providers/themeTokenProvider';
import {
  Integration, IntegrationProviders, IntegrationTypes, NavigationStyles,
} from '../../../interfaces';
import Sidebar from './sidebar';
import { useLayoutTokens } from './layout.tokens';
import { useAuthContext } from '../../../providers/ovApolloProvider';
import Downtime from '../../../components/layout/downtime';
import { getZendeskKeys, ZendeskWidget } from '../widgets/chatAgent/zendesk';
import { entityName } from '../../../util';
import { useGlobalToast } from '../../../providers/globalToastProvider';
import { PageConfigurationTypes } from '../../../interfaces/pageConfiguration';
import Disclaimer from './disclaimer';

const pageTypeMap: Record<string, PageConfigurationTypes> = {
  clients: PageConfigurationTypes.INDIVIDUAL,
  nonIndividualClients: PageConfigurationTypes.NON_INDIVIDUAL,
  households: PageConfigurationTypes.HOUSEHOLD,
};

const pageTypes = [PageConfigurationTypes.INDIVIDUAL, PageConfigurationTypes.NON_INDIVIDUAL, PageConfigurationTypes.HOUSEHOLD];

const Layout = () => {
  const navigate = useNavigate();
  const { showToast } = useGlobalToast();
  const { t } = useTranslation('workflowCompletions');
  const {
    userContext,
    displayMenuBar,
    closed,
    integrations,
    // ^^ is needed to conditionally load 3rd party integration scripts
    // e.g for Zendesk and Fraud Detection (Simility)
  } = useContext(UserContext);
  const { permissions } = usePermissions();
  const { appLogout } = useAuthContext();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const { sys, comp } = useThemeTokens(useLayoutTokens());

  const logout = async () => {
    await appLogout();
  };

  // This it to make pages scroll to top after every navigation
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // This it to redirect to root path on downtime
  useEffect(() => {
    if (userContext.applicableDowntimeSchedule) navigate('/');
  }, [navigate, userContext.applicableDowntimeSchedule]);

  const splitPath = pathname.split('/').filter(Boolean);
  const pageTypeFromPath = pageTypeMap[splitPath[splitPath.length - 2]];
  const hasNotifications = permissions.includes('read:notification');
  const isUnifiedClientExperience = userContext.role?.navigationStyle === NavigationStyles.SIMPLE;
  const appBarMobileAndTabletColor = comp.layout.backgroundColor;
  const appBarDesktopColor = comp.layout.backgroundColor;
  const hideAppBarActions = !!userContext.applicableDowntimeSchedule;

  // conditionally load the Simility fraud detection scripts
  useEffect(() => {
    const conditionallyInjectSimilityScripts = (orgIntegrations: Integration[]) => {
      const config = orgIntegrations.find((entry) => entry.type === IntegrationTypes.FRAUD_DETECTION
        && entry.provider === IntegrationProviders.SIMILITY)?.configuration;

      if (!config || !config.pageKeys || !config.fnclsValue || !config.scriptInjectURL) return;

      const CMID = window.sessionStorage.getItem('CMID');
      if (!CMID) {
        /* eslint-disable-next-line no-console */
        console.warn('Missing CMID. Will abort Simility fraud management initialization for ORG client!');
        return;
      }

      const { scriptInjectURL, fnclsValue, pageKeys = '' } = config;
      const pageKeysList = pageKeys.split(/\s*,\s*/);

      const page = pageKeysList.find((entry: string) => {
        const match = pathname.match(/\/+([a-zA-Z]+)\/?.*/);
        return match?.at(1) === entry;
      });

      if (!page) return;

      // add/update script data values for this path
      let dataIslandScript = document.querySelector('script[fncls]');
      if (!dataIslandScript) {
        dataIslandScript = document.createElement('script');
        dataIslandScript.setAttribute('type', 'application/json');
        dataIslandScript.setAttribute('fncls', fnclsValue);
        try {
          document.body.appendChild(dataIslandScript);
        } catch (err) {
          /* eslint-disable-next-line no-console */
          console.error('Error injecting fraud detection data script:', err);
        }
      }

      dataIslandScript.textContent = JSON.stringify({
        f: CMID,
        s: page.toUpperCase(),
      });

      // add Simility script
      let fraudnetInitScript = document.querySelector('#fraudnet-init');
      if (!fraudnetInitScript) {
        fraudnetInitScript = document.createElement('script');
        fraudnetInitScript.setAttribute('id', 'fraudnet-init');
        fraudnetInitScript.setAttribute('src', scriptInjectURL);
        try {
          document.body.appendChild(fraudnetInitScript);
        } catch (err) {
          /* eslint-disable-next-line no-console */
          console.error('Error injecting fraud detection init script:', err);
        }
      }
    };

    if (userContext) {
      conditionallyInjectSimilityScripts(integrations);
    }
  }, [pathname, userContext, integrations]);

  // handle potential redirect from custodian deposit transfer challenge
  // back into the OV web app
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const atbMeta = searchParams.get('atbmeta') || '';
    const statusParam = searchParams.get('status') || '';

    if (!atbMeta) return;

    let status: string | undefined;
    let transactionId: string | undefined;

    if (atbMeta.includes('|')) {
      // Format 1: pipe-separated within atbmeta
      // atbmeta=transactionId:1234567890|status:SUCCESS
      const metadata = atbMeta.split('|');
      status = metadata.find((entry) => entry.startsWith('status:'))?.split(':')[1]?.toUpperCase();
      transactionId = metadata.find((entry) => entry.startsWith('transactionId:'))?.split(':')[1];
    } else {
      // Format 2: transactionId in atbmeta, status as separate query param
      // atbmeta=transactionId:1234567890&status=SUCCESS
      const transactionIdMatch = atbMeta.match(/transactionId:([^&]+)/);
      transactionId = transactionIdMatch?.[1];
      status = statusParam.toUpperCase();
    }

    if (status && transactionId) {
      const storedTransactionId = window.localStorage.getItem('custodianRiskChallengeTransaction');
      if (transactionId === storedTransactionId) {
        datadogLogs.logger.info(
          'Detected redirect into OV app from custodian risk challenge',
          { status, transactionId },
        );
        // clean up challenge transaction ID from local store
        window.localStorage.removeItem('custodianRiskChallengeTransaction');

        // Use the timeouts to give the UI a chance to load before showing toasts
        if (status === 'SUCCESS') {
          setTimeout(() => {
            showToast({
              message: t('depositReview.success'),
              severity: 'success',
            });
          }, 3500);
        } else if (status === 'FAILURE') {
          setTimeout(() => {
            showToast({
              message: t('depositReview.failure'),
              severity: 'error',
            });
          }, 3500);
        } else if (status === 'LOGOUT' || status === 'LOCKED_OUT') {
          appLogout();
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pageOptions = () => {
    switch (pageTypeFromPath) {
      case PageConfigurationTypes.INDIVIDUAL:
        return userContext?.role?.individualPageConfiguration?.options;
      case PageConfigurationTypes.NON_INDIVIDUAL:
        return userContext?.role?.nonIndividualPageConfiguration?.options;
      case PageConfigurationTypes.HOUSEHOLD:
        return userContext?.role?.householdPageConfiguration?.options;
      default:
        return undefined;
    }
  };

  const options = pageOptions();

  return (
    <Box sx={{ display: 'flex' }}>
      {displayMenuBar && (
        <AppBar
          position='fixed'
          elevation={0}
          sx={{
            width: { md: '100%' },
            transition: 'all 0.3s',
            backgroundColor: { xs: appBarMobileAndTabletColor, md: appBarDesktopColor },
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <Box display='flex' flexDirection='row' alignItems='center'>
              {!hideAppBarActions && (
                <IconButton
                  onClick={() => setMobileOpen(!mobileOpen)}
                  size='small'
                  sx={{
                    color: comp.layout.textColor,
                    mr: 1,
                    display: { xs: !isUnifiedClientExperience ? 'block' : 'none', lg: 'none' },
                  }}
                >
                  <MenuIcon />
                </IconButton>
              )}
              <OrganizationLogo textColor={comp.layout.textColor} />
            </Box>
            <Box display='flex' justifyContent='end' alignItems='center'>
              {hasNotifications && !hideAppBarActions && <Notifications color={comp.layout.textColor} />}
              <EntityMenu logout={logout} showOnlyLogout={hideAppBarActions} />
            </Box>
          </Toolbar>
        </AppBar>
      )}

      <Box
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { md: '100%', width: '1vw' },
          maxWidth: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 48px)' },
          bgcolor: sys.color.background,
          mt: isUnifiedClientExperience && !displayMenuBar ? 0 : 7,
        }}
      >
        {userContext.applicableDowntimeSchedule ? (
          <>
            {!displayMenuBar && (
              <Box display='flex' flexDirection='column' alignContent={isUnifiedClientExperience ? 'center' : 'start'} flexWrap='wrap'>
                <Box display='flex' justifyContent='space-between' alignItems='center' maxWidth='1080px' width='100%' height='32px' mb={2} ml={{ xs: 0, sm: 2 }}>
                  <Box display='flex' justifyContent='end' alignItems='center' marginLeft='auto'>
                    <EntityMenu logout={logout} showOnlyLogout />
                  </Box>
                </Box>
              </Box>
            )}
            <Downtime downtime={userContext.applicableDowntimeSchedule} />
          </>
        ) : (
          <Box justifyContent='start' alignItems='center' sx={{ height: '100%' }} display='flex'>
            {!isUnifiedClientExperience && <Sidebar drawerWidth={closed ? 48 : 240} setMobileOpen={setMobileOpen} mobileOpen={mobileOpen} />}
            <Box
              width='100%'
              sx={{
                transition: 'all 0.4s',
                maxWidth: { lg: `calc(100% - ${!isUnifiedClientExperience ? `${closed ? 48 : 240}px` : '0px'})` },
                ml: {
                  xs: '0px',
                  sm: '0px',
                  md: '0px',
                  lg: !isUnifiedClientExperience ? `${closed ? 48 : 240}px` : '0px',
                },
              }}
            >
              {pageTypes.includes(pageTypeFromPath) && options?.enableContent && (
                <Disclaimer options={options} sx={{ maxWidth: !isUnifiedClientExperience ? `${options?.maxPageWidth || 1080}px` : '100%' }} />
              )}
              {/* Note: The breadcrumb for "UnifiedClientExperience" when "displayMenuBar" is set to true is displayed on pages such as viewPageVisual */}
              {!isUnifiedClientExperience && displayMenuBar && (
                <Box mb={3} mt={1}>
                  <AppBreadcrumbs />
                </Box>
              )}
              {!displayMenuBar && (
                <Box display='flex' flexDirection='column' alignContent={isUnifiedClientExperience ? 'center' : 'start'} flexWrap='wrap'>
                  <Box display='flex' justifyContent='space-between' alignItems='center' maxWidth='1080px' width='100%' height='32px' mb={2} ml={{ xs: 0, sm: 2 }}>
                    <AppBreadcrumbs />
                    <Box display='flex' justifyContent='end' alignItems='center' marginLeft='auto'>
                      {hasNotifications && <Notifications color={comp.layout.textColor} />}
                      <EntityMenu logout={logout} />
                    </Box>
                  </Box>
                </Box>
              )}
              {/* This is where actual component gets rendered (see appRoutes.tsx) */}
              <Outlet />
              {/* Temporary Zendesk workaround. Refactor when adding new chat providers. */}
              {userContext?.accessType && !isUndefined(getZendeskKeys(integrations)) && (
                <ZendeskWidget
                  widgetStyle={{ color: sys.color.primary }}
                  accessType={userContext?.accessType}
                  user={entityName(userContext)}
                  scriptKeys={getZendeskKeys(integrations)}
                />
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Layout;
